<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  background: $.upload("Background image"),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if("background pr"),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    "background pr",
  ),
  logo: $.upload("Logo or title artwork"),
  logoSize: $.select(
    "Logo size",
    [
      { label: "Small", value: "small" },
      { label: "Medium", value: "medium" },
      { label: "Large", value: "large" },
    ],
    { default: "medium" },
  ).if("logo pr"),
  title: $.text("Heading"),
  titleSize: $.select(
    "Heading size",
    [
      { label: "Small", value: "small" },
      { label: "Medium", value: "medium" },
      { label: "Large", value: "large" },
    ],
    { default: "medium" },
  ).if("title pr"),
  textColor: $.select(
    "Text color",
    [
      { label: "Light", value: "light" },
      { label: "Dark", value: "dark" },
    ],
    { default: "light" },
  ),
  content: $.richtext("Event details", {
    simple: true,
  }),
  addShadow: $.checkbox("Add shadow", { default: false }),
  buttonLabel: $.text("Button label"),
  buttonLink: $.text("Button destination").if("buttonLabel pr"),
  bottomImage: $.upload("Bottom decoration"),
  bottomImageSize: $.select(
    "Bottom decoration size",
    [
      { label: "Small", value: "small" },
      { label: "Medium", value: "medium" },
      { label: "Large", value: "large" },
    ],
    { default: "large" },
  ).if("bottomImage pr"),
});
export const EventHero = defineBlock({
  name: "EventHero",
  label: "Hero",
  icon: "celebration",
  thumbnail: "/block-thumbs/EventHero.svg",
  category: "Event",
  order: 20,
  schema,
})
</script>
<script setup lang="ts">
import { computed } from "vue";

import { Btn, pathKeyToURL, sanitizeHtml } from "@bagelink/vue"


;

const props = withDefaults(
  defineProps<{
    title?: string;
    titleSize?: "small" | "medium" | "large";
    textColor?: "light" | "dark";
    logo?: string;
    logoSize?: "small" | "medium" | "large";
    buttonLabel?: string;
    buttonLink?: string;
    bottomImage?: string;
    bottomImageSize?: "small" | "medium" | "large";
    dimBackground?: boolean;
    addShadow?: boolean;
    content?: string;
    background?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    /** Legacy fields keep previously saved heroes rendering after the schema simplification. */
    titleImage?: string;
    primaryLabel?: string;
    primaryLink?: string;
    poster?: string;
    artwork?: string;
  }>(),
  {
    title: "",
    titleSize: "medium",
    textColor: "light",
    logo: "",
    logoSize: "medium",
    buttonLabel: "",
    buttonLink: "",
    bottomImage: "",
    bottomImageSize: "large",
    dimBackground: false,
    addShadow: false,
    content: "",
    background: "",
    backgroundFit: "cover",
    titleImage: "",
    primaryLabel: "",
    primaryLink: "",
    poster: "",
    artwork: "",
  },
);

const backgroundUrl = computed(
  () => pathKeyToURL(props.background) || props.background,
);
const logoUrl = computed(() => {
  const source = props.logo || props.titleImage;
  return pathKeyToURL(source) || source;
});
const bottomImageUrl = computed(() => {
  const source = props.bottomImage;
  return pathKeyToURL(source) || source;
});
const resolvedButtonLabel = computed(() => props.buttonLabel);
const resolvedButtonLink = computed(() => props.buttonLink);
const titleClass = computed(
  () =>
    ({
      small: "txt-40 m_txt-28",
      medium: "txt-64 m_txt-40",
      large: "txt-80 m_txt-40",
    })[props.titleSize],
);
const logoClass = computed(
  () =>
    ({
      small: "w-250px",
      medium: "w-450px",
      large: "w-650px",
    })[props.logoSize],
);
const bottomImageClass = computed(
  () =>
    ({
      small: "w-450px",
      medium: "w-650px",
      large: "w-850px",
    })[props.bottomImageSize],
);
</script>

<template>
  <section
    class="relative isolation-isolate overflow-hidden px-2 py-4 m_px-1 m_py-2 txt-center"
    :class="
      textColor === 'light'
        ? 'color-white keep-white'
        : 'color-black keep-black'
    "
  >
    <img
      v-if="backgroundUrl"
      class="absolute inset w-100p h-100p z-0"
      :class="backgroundFit === 'fill' ? '' : backgroundFit"
      :src="backgroundUrl"
      alt=""
    />
    <div
      v-if="backgroundUrl && dimBackground"
      class="absolute inset bg-black opacity-4 z-0 pointer-events-none"
    />

    <div class="relative z-1 w-900px grid gap-1 justify-items-center">
      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="title"
        class="block w-100p m_w-100p"
        :class="[logoClass, addShadow ? 'shadow-txt' : '']"
      />

      <h1
        v-if="title"
        class="m-0 line-height-1 m_line-height-11 balance blox-special-heading txt-center"
        :class="[titleClass, addShadow ? 'shadow-txt' : '']"
      >
        {{ title }}
      </h1>

      <!-- eslint-disable vue/no-v-html -- Editor rich text is sanitized before rendering. -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div
        v-if="content"
        class="w-700px txt-20 m_txt-18 line-height-16 pretty txt-center"
        :class="addShadow ? 'shadow-txt' : ''"
        v-html="sanitizeHtml(content)"
      />

      <Btn
        v-if="resolvedButtonLabel && resolvedButtonLink"
        :value="resolvedButtonLabel"
        :to="resolvedButtonLink"
        lg
      />

      <img
        v-if="bottomImageUrl"
        :src="bottomImageUrl"
        alt=""
        class="block w-100p m_w-100p"
        :class="[bottomImageClass, addShadow ? 'shadow-txt' : '']"
      />
    </div>
  </section>
</template>
